import type { MoneyByCurrency } from './types';

/** Minor units to a currency string: 6799 GBP -> £67.99. */
export function money(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

/**
 * A MoneyByCurrency for a tile: the largest currency leads; others append.
 * Currencies are never blended into one number.
 */
export function moneyTotals(totals: MoneyByCurrency | null): string {
  if (totals === null) return '·'; // redacted for this role
  const entries = Object.entries(totals).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return money(0, 'GBP');
  return entries.map(([currency, minor]) => money(minor, currency)).join(' + ');
}

/** True for money that is genuinely zero, as distinct from redacted
 *  (null): the two must never share a rendering, because one is a fact
 *  about the business and the other a fact about your role. */
export function isZeroMoney(totals: MoneyByCurrency | null): boolean {
  return totals !== null && Object.values(totals).every((minor) => minor === 0);
}

/** Compact count: 1284 -> 1,284; 12900 -> 12.9K. */
export function compact(n: number): string {
  if (n < 10_000) return new Intl.NumberFormat('en-GB').format(n);
  return new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 })
    .format(n)
    .toUpperCase();
}

/** A raw entity id, shortened for display until a name is learned:
 *  "821b1d42-99d0-…" -> "821b1d42…". Never shown where a display name
 *  exists; the full id stays in the tooltip and the deep link. */
export function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

/** Relative time for the live feed: 42s ago, 7m ago, 3h ago. */
export function timeAgo(iso: string, now: () => number = Date.now): string {
  const seconds = Math.max(0, Math.floor((now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
